import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import InnerPage from '../components/InnerPage.jsx';
import { getPage } from '../data/pagesContent.js';
import { rewriteAssetUrlsInHtml } from '../utils/paths.js';

/**
 * FAQ category tabs — mirrors website/script.js initFaqPage for SPA HTML.
 */
function useFaqTabs(rootRef, html) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const tabs = [...root.querySelectorAll('[data-faq-tab]')];
    const panels = [...root.querySelectorAll('[data-faq-panel]')];
    const select = root.querySelector('#faqCategorySelect');
    if (!tabs.length || !panels.length) return undefined;

    const activate = (panelId) => {
      if (!panelId) return;
      tabs.forEach((tab) => {
        const on = tab.getAttribute('data-faq-tab') === panelId;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      panels.forEach((panel) => {
        panel.hidden = panel.id !== panelId;
      });
      if (select && select.value !== panelId) select.value = panelId;
    };

    const onTabClick = (e) => {
      activate(e.currentTarget.getAttribute('data-faq-tab'));
    };

    const onSelectChange = (e) => {
      activate(e.target.value);
    };

    tabs.forEach((tab) => tab.addEventListener('click', onTabClick));
    if (select) select.addEventListener('change', onSelectChange);

    const active =
      tabs.find((t) => t.classList.contains('is-active'))?.getAttribute('data-faq-tab') ||
      tabs[0]?.getAttribute('data-faq-tab');
    if (active) activate(active);

    return () => {
      tabs.forEach((tab) => tab.removeEventListener('click', onTabClick));
      if (select) select.removeEventListener('change', onSelectChange);
    };
  }, [rootRef, html]);
}

export default function FaqsPage() {
  const page = getPage('faqs');
  const contentRef = useRef(null);
  useFaqTabs(contentRef, page?.html);

  useEffect(() => {
    document.title = page?.title || 'FAQs | Nouakchott Oumtounsy International Airport (NKC/GQNO)';
  }, [page?.title]);

  if (!page) return null;

  return (
    <InnerPage
      slug={page.slug}
      heading={page.heading}
      lead={
        <>
          Find answers to commonly asked questions about travelling through Nouakchott Oumtounsy
          International Airport (NKC/GQNO). If you still need help, <Link to="/contact">contact us</Link>.
        </>
      }
      banner={page.banner}
      section={page.section}
    >
      <div ref={contentRef} dangerouslySetInnerHTML={{ __html: rewriteAssetUrlsInHtml(page.html) }} />
    </InnerPage>
  );
}
